import { getHitQualityByDistance, computePixelHitZones, getHitPoints, getStreakMultiplier, type HitQuality, type DifficultyConfig, type PixelHitZones } from '@/utils/timing';
import type { MorseSymbol } from '@/utils/morse';

export interface ScoreState {
  score: number;
  streak: number;
  maxStreak: number;
  multiplier: number;
  perfectHits: number;
  goodHits: number;
  okHits: number;
  misses: number;
  totalNotes: number;
}

export class ScoringSystem {
  private hitZones: PixelHitZones;
  private state: ScoreState;

  // Callbacks for UI updates
  private onScoreChange: ((state: ScoreState) => void) | null = null;
  private onStreakMilestone: ((streak: number) => void) | null = null;

  constructor(difficulty: DifficultyConfig, totalNotes: number) {
    this.hitZones = computePixelHitZones(difficulty);
    this.state = {
      score: 0,
      streak: 0,
      maxStreak: 0,
      multiplier: 1,
      perfectHits: 0,
      goodHits: 0,
      okHits: 0,
      misses: 0,
      totalNotes,
    };
  }

  /**
   * Process a hit attempt
   */
  processHit(
    pixelOffset: number,
    actualSymbol: MorseSymbol,
    expectedSymbol: MorseSymbol
  ): HitQuality {
    const quality = getHitQualityByDistance(pixelOffset, this.hitZones);
    const isCorrectSymbol = actualSymbol === expectedSymbol;

    if (quality !== 'miss' && isCorrectSymbol) {
      // Successful hit
      const points = getHitPoints(quality);
      const multiplier = getStreakMultiplier(this.state.streak);

      this.state.score += points * multiplier;
      this.state.streak += 1;
      this.state.multiplier = multiplier;

      if (this.state.streak > this.state.maxStreak) {
        this.state.maxStreak = this.state.streak;
      }

      // Track hit quality
      switch (quality) {
        case 'perfect':
          this.state.perfectHits++;
          break;
        case 'good':
          this.state.goodHits++;
          break;
        case 'ok':
          this.state.okHits++;
          break;
      }

      // Check for streak milestones
      this.checkStreakMilestone();
    } else {
      // Miss or wrong symbol
      this.processMiss();
      return 'miss';
    }

    this.notifyScoreChange();
    return quality;
  }

  /**
   * Process a miss (note passed without being hit)
   */
  processMiss(): void {
    this.state.misses++;
    this.state.streak = 0;
    this.state.multiplier = 1;
    this.notifyScoreChange();
  }

  /**
   * Check for streak milestones and notify
   */
  private checkStreakMilestone(): void {
    const milestones = [10, 25, 50, 100];
    if (milestones.includes(this.state.streak) && this.onStreakMilestone) {
      this.onStreakMilestone(this.state.streak);
    }
  }

  /**
   * Notify score change callback
   */
  private notifyScoreChange(): void {
    if (this.onScoreChange) {
      this.onScoreChange({ ...this.state });
    }
  }

  /**
   * Set callback for score changes
   */
  setOnScoreChange(callback: (state: ScoreState) => void): void {
    this.onScoreChange = callback;
  }

  /**
   * Set callback for streak milestones
   */
  setOnStreakMilestone(callback: (streak: number) => void): void {
    this.onStreakMilestone = callback;
  }

  /**
   * Get current score state
   */
  getState(): ScoreState {
    return { ...this.state };
  }

  /**
   * Calculate accuracy percentage
   */
  getAccuracy(): number {
    const hits = this.state.perfectHits + this.state.goodHits + this.state.okHits;
    const total = hits + this.state.misses;
    if (total === 0) return 100;
    return Math.round((hits / total) * 100);
  }

  /**
   * Get final results
   */
  getResults(): {
    score: number;
    accuracy: number;
    maxStreak: number;
    perfectHits: number;
    goodHits: number;
    okHits: number;
    misses: number;
  } {
    return {
      score: this.state.score,
      accuracy: this.getAccuracy(),
      maxStreak: this.state.maxStreak,
      perfectHits: this.state.perfectHits,
      goodHits: this.state.goodHits,
      okHits: this.state.okHits,
      misses: this.state.misses,
    };
  }
}
